import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';

// UDRAW-S1 §2 — click-to-edit rich text field for the WorkItemDrawer's
// Description (deliverable) and Acceptance criteria fields. Renders the
// stored HTML sanitised; on click swaps to a mini editor (B / I / U /
// UL / OL via document.execCommand on a contenteditable div) + Save /
// Cancel. Save flushes ONLY this field via the caller-supplied onSave
// (a promise-returning function that hits the existing PATCH endpoint
// on the same {deliverable|acceptanceCriteria} column). Cancel discards
// and shows the last-saved value.
//
// - Storage: HTML string in the same columns; 2000-char limit enforced
//   BEFORE the write (visible error, no request fires).
// - Sanitisation: DOMPurify with a small whitelist (b/i/u/em/strong/
//   ul/ol/li/p/br/div/span). Plain-text stored earlier renders as-is
//   (plain text is valid HTML).
// - Closed WI: readOnly=true → renders text only, no click affordance.

const ALLOWED_TAGS = ['b', 'i', 'u', 'em', 'strong', 'ul', 'ol', 'li', 'p', 'br', 'div', 'span'];
const ALLOWED_ATTR = [];

export function sanitizeHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(String(html), { ALLOWED_TAGS, ALLOWED_ATTR });
}

export default function RichTextField({
  testId,
  value,
  placeholder = 'Click to add…',
  readOnly = false,
  maxLength = 2000,
  onSave, // async (html) => void; throw to signal error
  // UDRAW-S5 §2.2.4 — optional editing-lifecycle hooks. The parent
  // uses them to fire StartEditing / StopEditing on the SignalR hub
  // so peers see the "X is editing…" indicator. Called with no
  // arguments; wrap in a stable callback if you care.
  onEditingStart,
  onEditingStop,
}) {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (editing && editorRef.current) {
      // Seed the editor from the current stored value on every open;
      // sanitize to prevent a stale unsanitised chunk from surviving.
      editorRef.current.innerHTML = sanitizeHtml(value);
      editorRef.current.focus();
      if (typeof onEditingStart === 'function') {
        try { onEditingStart(); } catch { /* ignore */ }
      }
      // Best-effort StopEditing when the editor unmounts before Save
      // or Cancel (e.g. drawer closed via the ✕ button). doSave /
      // doCancel below also call it explicitly.
      return () => {
        if (typeof onEditingStop === 'function') {
          try { onEditingStop(); } catch { /* ignore */ }
        }
      };
    }
    return undefined;
  }, [editing]);  // eslint-disable-line react-hooks/exhaustive-deps

  const isEmpty = !value || !String(value).replace(/<[^>]*>/g, '').trim();

  if (readOnly) {
    return (
      <div
        data-testid={testId ? `${testId}-view` : undefined}
        className={`udraw-ed-view${isEmpty ? ' empty' : ''}`}
      >
        {isEmpty
          ? <span data-testid={testId ? `${testId}-placeholder` : undefined}>—</span>
          : <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />}
      </div>
    );
  }

  if (!editing) {
    return (
      <div
        data-testid={testId ? `${testId}-view` : undefined}
        className={`udraw-ed-view${isEmpty ? ' empty' : ''}`}
        onClick={() => { setError(''); setEditing(true); }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); } }}
      >
        {isEmpty
          ? <span data-testid={testId ? `${testId}-placeholder` : undefined}>{placeholder}</span>
          : <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />}
      </div>
    );
  }

  const exec = (cmd) => (e) => {
    // Keep focus + selection inside the contenteditable — the button's
    // default mousedown steals focus otherwise, which drops the caret.
    e.preventDefault();
    document.execCommand(cmd, false, null);
  };

  const doSave = async () => {
    const rawHtml = editorRef.current?.innerHTML || '';
    const cleaned = sanitizeHtml(rawHtml);
    if (cleaned.length > maxLength) {
      setError(`Too long (${cleaned.length}/${maxLength} characters). Trim and try again.`);
      return;
    }
    if (typeof onSave === 'function') {
      setSaving(true);
      try {
        await onSave(cleaned);
        setError('');
        setEditing(false);
      } catch (err) {
        setError(err?.message || 'Could not save.');
      } finally {
        setSaving(false);
      }
    } else {
      setEditing(false);
    }
  };

  const doCancel = () => {
    setError('');
    setEditing(false);
  };

  return (
    <div className="udraw-rte" data-testid={testId}>
      <div className="udraw-rtb" role="toolbar">
        <button type="button" onMouseDown={exec('bold')}       data-testid={testId ? `${testId}-b` : undefined}><b>B</b></button>
        <button type="button" onMouseDown={exec('italic')}     data-testid={testId ? `${testId}-i` : undefined}><i>I</i></button>
        <button type="button" onMouseDown={exec('underline')}  data-testid={testId ? `${testId}-u` : undefined}><u>U</u></button>
        <button type="button" onMouseDown={exec('insertUnorderedList')} data-testid={testId ? `${testId}-ul` : undefined}>• ≡</button>
        <button type="button" onMouseDown={exec('insertOrderedList')}   data-testid={testId ? `${testId}-ol` : undefined}>1. ≡</button>
      </div>
      <div
        ref={editorRef}
        className="udraw-rta"
        contentEditable
        suppressContentEditableWarning
        data-testid={testId ? `${testId}-editor` : undefined}
        onInput={() => { /* keep the DOM live; save on click */ }}
      />
      {error && (
        <div className="udraw-ed-err" data-testid={testId ? `${testId}-err` : undefined}>{error}</div>
      )}
      <div className="udraw-ed-actions">
        <button type="button" className="udraw-mini pri" onClick={doSave} disabled={saving}
                data-testid={testId ? `${testId}-save` : undefined}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="udraw-mini" onClick={doCancel} disabled={saving}
                data-testid={testId ? `${testId}-cancel` : undefined}>
          Cancel
        </button>
      </div>
    </div>
  );
}
